import { useTranslation } from 'react-i18next'
import React, { FC } from 'react'
import TablePagination, { TablePaginationProps } from '@material-ui/core/TablePagination'
import IconButton from '@material-ui/core/IconButton'
import { FirstPage, LastPage, KeyboardArrowLeft, KeyboardArrowRight } from '@material-ui/icons'

// Shared, Hebrew-localized pagination used across every table view so they all
// look identical: "{from}-{to} מתוך {count}" for the displayed-rows text,
// "שורות בעמוד:" for the rows-per-page label, and first / prev / next / last
// navigation buttons (MUI v4 core ships only prev/next, so we provide our own
// ActionsComponent). All TablePagination props still pass through and any
// explicit prop (e.g. a custom labelRowsPerPage) overrides these defaults.

type ActionsProps = {
  count: number
  page: number
  rowsPerPage: number
  className?: string
  onPageChange: (event: React.MouseEvent<HTMLButtonElement> | null, page: number) => void
}

const PaginationActions: FC<ActionsProps> = ({ count, page, rowsPerPage, className, onPageChange }) => {
  const { t } = useTranslation('app')
  const lastPage = Math.max(0, Math.ceil(count / rowsPerPage) - 1)
  const isFirst = page <= 0
  const isLast = page >= lastPage

  // direction: ltr keeps the buttons in a fixed |< < > >| visual order
  // regardless of the surrounding RTL layout.
  return (
    <div className={className} style={{ display: 'flex', flexShrink: 0, direction: 'ltr' }}>
      <IconButton size="small" aria-label={t`app:page_first`} disabled={isFirst} onClick={e => onPageChange(e, 0)}>
        <FirstPage />
      </IconButton>
      <IconButton size="small" aria-label={t`app:page_prev`} disabled={isFirst} onClick={e => onPageChange(e, page - 1)}>
        <KeyboardArrowLeft />
      </IconButton>
      <IconButton size="small" aria-label={t`app:page_next`} disabled={isLast} onClick={e => onPageChange(e, page + 1)}>
        <KeyboardArrowRight />
      </IconButton>
      <IconButton size="small" aria-label={t`app:page_last`} disabled={isLast} onClick={e => onPageChange(e, lastPage)}>
        <LastPage />
      </IconButton>
    </div>
  )
}

// Props pass straight through to MUI's TablePagination. The generic is widened
// to React.ElementType so the `component="div"` string every call site passes
// stays assignable, while the handler props keep their contextual types.
const MekomeTablePagination: FC<TablePaginationProps<React.ElementType>> = props => {
  const { t } = useTranslation('app')

  return (
  <TablePagination
    labelRowsPerPage={t`app:rows_per_page`}
    labelDisplayedRows={({ from, to, count }) =>
      `${from}-${to} ${t`app:pagination_of`} ${count !== -1 ? count : t('app:pagination_more_than', { to })}`
    }
    ActionsComponent={PaginationActions as any}
    {...props}
  />
  )
}

export default MekomeTablePagination
